import { NavBar } from '@opengov/bnp-component-navbar';
import {
  getSessionEventContext,
  type SignOutMethod,
  trackSignedOut,
  useTrackSignedIn
} from '@opengov/bnp-core';
import React from 'react';

import type { AnalyticsEntity, AnalyticsSession } from '../../../instrumentation/storiesAnalyticsRuntime';
import { useStoriesAnalytics } from '../../../hooks/useStoriesAnalytics';

/** `activeAppKey` on the navbar and `app_key` on Stories' session events. */
const ACTIVE_APP_KEY = 'stories';

/** `source_app` on Stories' session events, telling them apart from bnp-ui and Zeus. */
const ANALYTICS_SOURCE = 'stories';

type NavBarProps = React.ComponentProps<typeof NavBar>;
type Auth0Config = { auth0domain: string; auth0clientID: string; auth0callbackUrl: string };

type Props = Omit<NavBarProps, 'handleSignOut' | 'contextSwitcherOptions' | 'activeAppKey'> & {
  session: AnalyticsSession;
  entity: AnalyticsEntity;
  contextSwitcherOptions: Omit<NonNullable<NavBarProps['contextSwitcherOptions']>, 'auth0Config'>;
  /** Standalone Auth0 settings; `onAuth0SessionExpired` is wired here. */
  auth0Config?: Auth0Config;
  handleSignOut: () => void;
  isPosthogEnabled: boolean;
  isPspMode: boolean;
  routePattern?: string;
};

export function StoriesNavBar({
  session,
  entity,
  contextSwitcherOptions,
  auth0Config,
  handleSignOut,
  isPosthogEnabled,
  isPspMode,
  routePattern,
  ...navBarProps
}: Props) {
  // `undefined` unless the PostHog flag is on, Stories is standalone, and the key is
  // present (integration only) — then the bnp-core helpers below send nothing.
  const analytics = useStoriesAnalytics({
    session,
    entity,
    isPosthogEnabled,
    isPspMode,
    pathname: window.location.pathname,
    routePattern
  });
  useTrackSignedIn({ client: analytics, userUuid: session.uuid, appKey: ACTIVE_APP_KEY, source: ANALYTICS_SOURCE });

  // trackSignedOut waits (capped at 1s, never throws) for the event to flush,
  // because the sign-out redirect kills any in-flight request.
  const signOut = async (method: SignOutMethod) => {
    if (analytics) {
      await trackSignedOut(analytics, method, getSessionEventContext(ACTIVE_APP_KEY, ANALYTICS_SOURCE));
    }
    handleSignOut();
  };
  const handleTrackedSignOut = () => {
    void signOut('auth0');
  };
  // A silent Auth0 expiry is not a deliberate sign-out; tracked under its own method.
  const handleSessionExpired = () => {
    void signOut('session_expired');
  };

  return (
    <NavBar
      {...navBarProps}
      activeAppKey={ACTIVE_APP_KEY}
      contextSwitcherOptions={{
        ...contextSwitcherOptions,
        auth0Config: auth0Config ? { ...auth0Config, onAuth0SessionExpired: handleSessionExpired } : undefined
      }}
      handleSignOut={handleTrackedSignOut}
    />
  );
}
